<template>
  <!-- add a wrap to single map for layout purpose. compare map can't be wraped. required class compare-map, otherwise display won't work-->
  <div style="position: relative;" :style="{ width: widthPct + '%', height: height + 'px'}" v-if="isSingleMap">
    <div :id="mapDivId" class="map-container" style="width:100%; height: 100%">
      <!-- this is a loading spinning animation -->
      <spinning-icon v-show="bShowLoading"/>
      <transition name="fade">
        <div v-show="bShowOverlay" class="overlay-msg" @wheel.prevent="onScroll($event)">
          <div class="overlay-text">Use Ctrl + Scroll to zoom the map</div>
        </div>
      </transition>
    </div>
    <div v-if="mapConfig?.showMapControl || false" :id="mapDivId + 'controller'" class="custom-controller">
    </div>
  </div>
  <template v-else>
    <div :id="mapDivId" class="map-container compare-map" :style="{ width: widthPct + '%', height: height + 'px'}">
      <!-- this is a loading spinning animation -->
      <spinning-icon v-show="bShowLoading"/>
      <transition name="fade">
        <div v-show="bShowOverlay" class="overlay-msg" @wheel.prevent="onScroll($event)">
          <div class="overlay-text">Use Ctrl + Scroll to zoom the map</div>
        </div>
      </transition>
      <div v-show="mapTitle" class="overlay-title" :class="[mapTitleClass]">
        <div class="cf5">{{ mapTitle }}</div>
      </div>
    </div>
    <div v-if="mapConfig?.showMapControl || false" :id="mapDivId + 'controller'" class="custom-controller">
    </div>
  </template>
  <!-- legend -->
  <div class="legend-container" :style="{width: (100 - widthPct - 10) + '%', 'margin-left': mapConfig?.showMapControl ? '60px' : '10px'}" v-if="includeLegend">
    <template v-for="(item, index) in legends" :key="'legend' + index">
    <!-- single layer, one checkbox on header rows-->
      <div class="legendRow">
        <div class="legend-header">
          <label class="checkbox-label" v-if="showCheckBox">
            <input type="checkbox" @change="showHideLayer($event, item.layerId)" v-model="item.isVisible"  >
            <span class="checkbox-custom rectangular"></span>
          </label>
          <legend-row :single-style="item.singleStyle" :legend-row="item"/> <!-- display header, some layers need headers -->
        </div>
        <legend-row :list-style="item.listStyle" :legend-row="item"/> <!-- display details, if list style is not null will display extra list -->
      </div>
    </template>
  </div>
</template>
<script>
/* --------------------------------------------------------
Basic unit to create a map
props:
  mapDivId: need to be unique among all map containers within single page
  mapConfig: information about map display, show legend? show controls etc.
  mayLayers: layers to be loaded.
  Based on passing parameter: mapLayers (AOI layer infor, aoiId, and other data layer list),
  . read map data from API (based on mapId) and package each map layer to mapbox configuration object
  . gather legends and display legend items
  . rendering maps
  . when all maps rendered, move aoi to the top and then apply a layer mask.
-------------------------------------------------------- */
import { reactive, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import SingleLegend from './SingleLegend.vue';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import * as mapUtil from './map-util.js';
import SpinningIcon from './SpinningIcon.vue';

export default {
  name: 'standard-map',
  props: {
    mapConfig: { type: Object, required: false },
    mapLayers: { type: Object, required: true },
    mapDivId: { required: true, type: String },
    aoiData: { required: true, type: Object },
    padding: { type: Number, required: false, default: 5 },
    width: { type: Number, required: true, default: 600 }, // width in px
    widthPct: { type: Number, required: true, default: 100 }, // width in pct
    height: { type: Number, required: true, default: 600 },
    boundaryColor: { type: String, required: false, default: '#fff' },
    isSingleMap: { type: Boolean, required: false, default: true },
    mapTitle: { type: String, required: false },
    mapTitleClass: { type: String, required: false },
  },
  components: { 'legend-row': SingleLegend, SpinningIcon },
  emits: ['map-loaded', 'init'],
  setup (props, { emit }) {
    const accessToken = import.meta.env.VITE_APP_MAPBOX_TOKEN;
    const legends = reactive([]);
    const bShowLoading = ref(true);
    const includeControl = ref(false);
    const includeLegend = ref(false);
    const showCheckBox = ref(false);
    const bShowOverlay = ref(false);

    const bAoiLoaded = ref(false);
    const nEventFired = ref(0);

    const excludeTextFromLegend = props.mapConfig.excludeTextFromLegend || '';

    let map;
    onMounted(() => {
      const mapConfig = props.mapConfig;
      includeControl.value = mapConfig.showMapControl;
      includeLegend.value = mapConfig.showLegend;
      showCheckBox.value = mapConfig.allowHideLayer;


      mapboxgl.accessToken = accessToken;
      map = new mapboxgl.Map({
        container: props.mapDivId,
        // style: 'mapbox://styles/mapbox/light-v9',
        style: { version: 8, sources: {}, layers: [] },
        // zoom: 6,
        // center: [-114.6309516, 56.6293613],
        bounds: props.aoiData.bbox,
        fitBoundsOptions: {
          padding: props.padding
        },
        attributionControl: false,
        preserveDrawingBuffer: true // needed for export to image
      });
      map.setMaxBounds(map.getBounds());
      map.dragRotate.disable();
      // disable map rotation using touch rotation gesture
      map.touchZoomRotate.disableRotation();
      map.scrollZoom.disable();
      map.scrollZoom.setWheelZoomRate(0.02); // Default 1/450

      if (includeControl.value) {
        const zoomToExtent = new mapUtil.MapboxGLButtonControl({
          className: 'mapbox-zoom-to-extent',
          title: 'Zoom to Extent',
          eventHandler: function () {
            if (props.aoiData.bbox) {
              map.fitBounds(props.aoiData.bbox, { padding: { top: props.padding, bottom: props.padding, left: props.padding, right: props.padding } });
            }
          }
        });
        const zoomIn = new mapUtil.MapboxGLButtonControl({
          className: 'mapboxgl-ctrl-zoom-in',
          title: 'Zoom in',
          eventHandler: function () {
            map.zoomIn();
          }
        });
        const zoomOut = new mapUtil.MapboxGLButtonControl({
          className: 'mapboxgl-ctrl-zoom-out',
          title: 'Zoom out',
          eventHandler: function () {
            map.zoomOut();
          }
        });
        /* Add Controls to the Map, using custom containers outside of map area */
        const controlerContainer = document.getElementById(props.mapDivId + 'controller');
        controlerContainer.appendChild(zoomIn.onAdd(map));
        controlerContainer.appendChild(zoomOut.onAdd(map));
        controlerContainer.appendChild(zoomToExtent.onAdd(map));
        /* default controllers on the map
          map.addControl(zoomToExtent, 'bottom-right');
          map.addControl(zoomOut, 'bottom-right');
          map.addControl(zoomIn, 'bottom-right');
          // map.addControl(new mapboxgl.FullscreenControl());
          // map.addControl(new mapboxgl.NavigationControl({ showCompass: false, visualizePitch: false }), 'bottom-right');
        */
        map.addControl(new mapboxgl.ScaleControl({
          maxWidth: 80,
          unit: 'metric'
        }), 'bottom-left');

        map.on('wheel', event => {
          if (event.originalEvent.ctrlKey) { // Check if CTRL key is pressed
            event.originalEvent.preventDefault(); // Prevent chrome/firefox default behavior
            if (!map.scrollZoom._enabled) map.scrollZoom.enable(); // Enable zoom only if it's disabled
            bShowOverlay.value = false;
          } else {
            if (map.scrollZoom._enabled) map.scrollZoom.disable(); // Disable zoom only if it's enabled
            bShowOverlay.value = true;
          }
        });
      } else {
        map.scrollZoom.disable();
        // disable map rotation using touch rotation gesture
        map.touchZoomRotate.disable();
      }
      map.on('load', () => {
        loadLayer(map);
        emit('init', map); // need this for compare map
      });

      map.on('zoom', () => {
        bShowOverlay.value = false;
      });

      map.on('data', function (data) {
        if (data.dataType === 'source' && data.isSourceLoaded) {
          bShowLoading.value = false;

          if (data.sourceId == props.aoiData.aoiId) {
            // console.log('Map aoi loaded', props.aoiData.aoiId)
            bAoiLoaded.value = true;
            // context.emit('map-loaded', { map, aoiData: props.aoiData});
          }
        }
      })

      map.on('idle', function () {
        bShowLoading.value = false;
        fireMapReadyEvent();
      });
      map.on('moveend', function () { // used for export image. don't resize window etc.
        bShowLoading.value = false;

        fireMapReadyEvent();
      });
      // add event to resize map, adding the event listener...
      window.addEventListener('resize', redraw)
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', redraw)
    });
    const onScroll = (event) => {
      if (event.ctrlKey) { // Check if CTRL key is pressed
        event.preventDefault(); // Prevent chrome/firefox default behavior
        bShowOverlay.value = false;
      } else {
        bShowOverlay.value = true;
      }
    };

    const fireMapReadyEvent = () => {
      if (nEventFired.value === 0) {
        emit('map-loaded', { map, aoiData: props.aoiData });

        nEventFired.value++;
        // console.log('Map ready to export') // trigger mulitple times
      } else {
        // console.log('Map ready to export No ', nEventFired.value ? 'fired already':'', bAoiLoaded.value ? '':'Aoi not ready')
      }
    }
    /* get data from layers:
    add layers, collect legend information
    */
    const loadAOILayer = async (baseLayer) => {
      bShowLoading.value = true;

      const newLayer = mapUtil.generateAOIConfig(baseLayer, props.boundaryColor);
      map.addLayer(newLayer);
      /* in most cases, props.aoiId == baselayer.id, but some case may not */
      const layerId = baseLayer.id + '';

      const result = await mapUtil.checkSourceLoaded(layerId, map); // })
      return result;
    }
    /* load other data, first get data from API, and then create mapbox config and legend items */
    const loadLayerFromData = async (id, customName, _includeLegend) => {
      bShowLoading.value = true;
      const isAOI = (props.aoiId === id);
      const baseLayer = await mapUtil.loadDataFromUrl(id, isAOI);
      if (!baseLayer) {
        return {};
      }
      const displayLegendName = customName || baseLayer.name;
      let localLegendIndex = 0;

      if (_includeLegend) {
        localLegendIndex = legends.findIndex(x => x.layerId === id);
        legends[localLegendIndex].name = displayLegendName.replace(excludeTextFromLegend, ''); // usually one
        legends[localLegendIndex].layerId = baseLayer.id + '';
      }
      let layerId = baseLayer.id + '';
      /*
        when no style specified, the style will come from settings, mostly like to be polygon AOI layer
      */
      if (!baseLayer.style) {
        const newLayerConfig = mapUtil.generateConfig(baseLayer);
        map.addLayer(newLayerConfig);
        if (_includeLegend) legends[localLegendIndex].singleStyle = baseLayer.settings;
      } else {
        /*
          likely to be a vector-tiled map, where layer information (id, styling, rendering) stored under style.layers
          only handle first layer, not to include all layers.
        */
        layerId = baseLayer.id + '';
        const layer = baseLayer.style.layers[0];
        const source = baseLayer.style.sources.dataset;
        layer.source = layerId;
        map.addSource(baseLayer.id, source);

        map.addLayer(layer);
        const fill = { fill: layer.type === 'fill', color: layer.paint[layer.type + '-color'], border: layer.type === 'line' };
        if (_includeLegend) {
          legends[localLegendIndex].singleStyle = fill;
          legends[localLegendIndex].listStyle = mapUtil.collectLegend(baseLayer.style.layers);
          legends[localLegendIndex].layerId = layer.id;
        }
      }
      /* return when the source done loading */
      const result = await mapUtil.checkSourceLoaded(layerId, map);
      return result;
    };

    /* load layers and add AOI layers
      after all layers rendered, draw mask and move AOI to top
    */
    const loadLayer = (inMap) => {
      map = inMap;
      const aoiLayer = props.aoiData.data;
      const aoiId = String(props.aoiData.aoiId);

      mapUtil.drawMask(map, aoiLayer.geom);
      /* loop through the layer ids, to collection legend ids so legends will be displayed in the desired orders
      and then start to call APIs to get map layer information and render them */
      const renderingTasks = [];
      renderingTasks.push(loadAOILayer(aoiLayer));

      (props.mapLayers || []).forEach((layer) => {
        const id = String(layer.mapId);
        legends.push({ layerId: id, isVisible: true });
        renderingTasks.push(loadLayerFromData(id, layer.customMapName, layer.showInLegend));
      });
      // don't include AOI in Legend
      // if (includeLegend) {
      //   legends.push({ id: aoiId, layerId: aoiId, isVisible: true, name: aoiLayer.name, singleStyle: { border: true, color: props.boundaryColor, fill: false } });
      // }

      Promise.all(renderingTasks).then((loadedLayers) => {
        if (props.mapLayers.length > 1) {
          /*
            Make sure the layer follows the input order, the first one appear on top, last one
            on bottom.
            arr.reduceRight(function(accumulator, currentValue, index, array)
          */
          props.mapLayers.reduceRight((_, layer) => map.moveLayer(layer.mapId));
        }

        // move aoi to front, and move a mask layer on top
        map.moveLayer(aoiId);
        map.moveLayer('world');
        // show /hide based on the toShow property values
        batchShowHideLayers();
        /* redraw map to correct its size */
        map.resize();
      });
    };
    /* toggle layers */
    const showHideLayer = ($event, layerId) => {
      if (layerId instanceof Array) {
        layerId.forEach(x => mapUtil.toggleLayer(map, x.id, $event.target.checked));
        layerId.forEach(x => (x.isVisible = $event.target.checked));
      } else {
        mapUtil.toggleLayer(map, layerId, $event.target.checked);
      }
      bShowLoading.value = true;
    };

    const batchShowHideLayers = () => {
      props.mapLayers.forEach(layerObj => {
        /* get sublayer id based on the layer mapId */

        const layerId = legends.find(x => x.layerId + '' === layerObj.mapId + '').layerId;
        mapUtil.toggleLayer(map, layerId + '', layerObj.toShow);
      });
      bShowLoading.value = true;
    };

    const redraw = () => {
      if (map) {
        /* redraw map to correct its size */
        map.resize();
        if (props.aoiData.bbox) {
          map.fitBounds(props.aoiData.bbox, { padding: { top: props.padding, bottom: props.padding, left: props.padding, right: props.padding } });
        }
      }
    };
    watch(bShowOverlay, () => {
      if (bShowOverlay.value) {
        setTimeout(() => {
          bShowOverlay.value = false;
          // -> logs 1
        }, 2000);
      }
    });

    return {
      redraw,
      loadLayer,
      bShowLoading,
      includeControl,
      includeLegend,
      showCheckBox,
      legends,
      showHideLayer,
      bShowOverlay,
      onScroll,
      batchShowHideLayers
    }
  },
  /* control layers from outside parameters */
  watch: {
    mapLayers: {
      // This will let Vue know to look inside the array
      deep: true,

      // We have to move our method to a handler field
      handler (old, newVal) {
        this.batchShowHideLayers();
      }
    }
  }
}
</script>

<style scoped>
@import "./map.css";
div ::v-deep(.mapboxgl-compare) {
  background-color: #9e9e9e7d;
}
</style>
